"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useCallback, useMemo, useState } from "react";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChevronsUpDown, ImageIcon, Loader2, MapPin, Palette } from "lucide-react";
import { CountryCodes } from "@/utils/country-codes";
import { Timezones } from "@/utils/timezones";
import { HexColorPicker } from "react-colorful";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { LEDGER_CURRENCY_OPTIONS } from "@/components/manage-workspace-modal/sections/manage-ledgers/ledger-currencies";
import { LogoUploadValidations } from "@/components/common/file-picker/upload-validations";
import { uploadFileWithValidation } from "@/components/common/file-picker/upload-with-validation";
import { SDK } from "@/@sdk";

export const workspaceBasicsSchema = z.object({
  brandName: z.string().min(1, "Workspace name is required"),
  logo: z.string().optional(),
  website: z.string().url("Invalid URL").optional().or(z.literal("")),
  primaryColor: z
    .string()
    .regex(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, "Invalid color code"),
  country: z.string().min(1, "Country is required"),
  timezone: z.string().min(1, "Timezone is required"),
  primaryCurrency: z.string().min(1, "Primary currency is required"),
});

export type WorkspaceBasicsFormValues = z.infer<typeof workspaceBasicsSchema>;

type WorkspaceBasicsFormProps = {
  organizationId?: string;
  defaultValues: WorkspaceBasicsFormValues;
  primaryCurrencyLocked?: boolean;
  mode?: "settings" | "wizard";
  submitLabel?: string;
  isSubmitting?: boolean;
  onSubmit: (values: WorkspaceBasicsFormValues) => Promise<void>;
};

export function WorkspaceBasicsForm({
  organizationId,
  defaultValues,
  primaryCurrencyLocked = false,
  mode = "settings",
  submitLabel = "Save changes",
  isSubmitting = false,
  onSubmit,
}: WorkspaceBasicsFormProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [openCountry, setOpenCountry] = useState(false);
  const [openTimezone, setOpenTimezone] = useState(false);
  const [openCurrency, setOpenCurrency] = useState(false);

  const form = useForm<WorkspaceBasicsFormValues>({
    resolver: zodResolver(workspaceBasicsSchema),
    defaultValues,
  });

  const countryOptions = useMemo(
    () =>
      CountryCodes.map((country) => ({
        label: country.name,
        value: country.code,
      })),
    []
  );

  const timezoneOptions = useMemo(
    () =>
      Timezones.map((timezone) => ({
        label: timezone,
        value: timezone,
      })),
    []
  );

  const countryLabelByCode = useMemo(() => {
    const map: Record<string, string> = {};
    for (const country of countryOptions) {
      map[country.value] = country.label;
    }
    return map;
  }, [countryOptions]);

  const logoValue = form.watch("logo");
  const primaryColorValue = form.watch("primaryColor");

  const uploadLogo = useCallback(
    (file: File) => {
      if (!organizationId) return;

      setIsUploading(true);
      uploadFileWithValidation({
        file,
        reference: {
          type: "ORGANIZATION",
          entityId: organizationId,
        },
        uploadFileFunction: (uploadFile: File) =>
          SDK.uploadFile(
            uploadFile,
            {
              type: "ORGANIZATION",
              entityId: organizationId,
            },
            []
          ),
        validations: LogoUploadValidations,
        resizeBeforeUpload: {
          maxWidth: 1080,
          maxHeight: 1080,
          compressFormat: "PNG",
        },
      })
        .then((uploaded) => {
          form.setValue("logo", uploaded.url, { shouldDirty: true });
        })
        .finally(() => {
          setIsUploading(false);
        });
    },
    [form, organizationId]
  );

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-5"
        id="workspace-basics-form"
      >
        <FormField
          control={form.control}
          name="brandName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Workspace name</FormLabel>
              <FormControl>
                <Input placeholder="Enter workspace name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="website"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Website</FormLabel>
              <FormControl>
                <Input placeholder="https://example.com" {...field} />
              </FormControl>
              <FormDescription>
                Your agency website URL used across the workspace.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="logo"
          render={() => (
            <FormItem>
              <FormLabel>Logo</FormLabel>
              <FormControl>
                <div
                  className={cn(
                    "flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-4",
                    logoValue ? "bg-muted/20" : "bg-muted/10"
                  )}
                >
                  {logoValue ? (
                    <img
                      src={logoValue}
                      alt="Workspace logo preview"
                      className="h-16 w-16 rounded-md border bg-background object-contain p-2"
                    />
                  ) : (
                    <div className="flex h-16 w-16 items-center justify-center rounded-md border bg-background">
                      <ImageIcon className="h-7 w-7 text-muted-foreground" />
                    </div>
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadLogo(file);
                      e.target.value = "";
                    }}
                    className="hidden"
                    id="workspace-basics-logo-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      document
                        .getElementById("workspace-basics-logo-upload")
                        ?.click()
                    }
                    disabled={isUploading || !organizationId}
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      "Choose file"
                    )}
                  </Button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="primaryColor"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Palette className="h-4 w-4 text-muted-foreground" />
                Primary brand color
              </FormLabel>
              <FormControl>
                <div className="flex flex-wrap items-center gap-3">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="h-10 w-10 rounded-md p-0"
                        style={{ backgroundColor: field.value }}
                      >
                        <span className="sr-only">Pick a color</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-3" align="start">
                      <HexColorPicker
                        color={field.value}
                        onChange={field.onChange}
                      />
                    </PopoverContent>
                  </Popover>
                  <Input
                    {...field}
                    placeholder="#000000"
                    className="w-[140px] font-mono uppercase"
                  />
                  <div
                    className="hidden h-10 min-w-[120px] flex-1 items-center rounded-md border px-3 text-sm sm:flex"
                    style={{
                      borderColor: primaryColorValue,
                      color: primaryColorValue,
                    }}
                  >
                    Preview accent
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="primaryCurrency"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Primary currency</FormLabel>
              <Popover open={openCurrency} onOpenChange={setOpenCurrency}>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      disabled={primaryCurrencyLocked}
                      className={cn(
                        "w-full justify-between font-normal sm:max-w-xs",
                        !field.value && "text-muted-foreground"
                      )}
                    >
                      <span className="truncate">
                        {field.value || "Select currency"}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-[min(280px,90vw)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search currency..." />
                    <CommandEmpty>No currency found.</CommandEmpty>
                    <CommandGroup className="max-h-[300px] overflow-auto">
                      {LEDGER_CURRENCY_OPTIONS.map((currency: string) => (
                        <CommandItem
                          value={currency}
                          key={currency}
                          onSelect={() => {
                            field.onChange(currency);
                            setOpenCurrency(false);
                          }}
                        >
                          {currency}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
              <FormDescription>
                Used for workspace default ledgers and trade expense posting.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  Country
                </FormLabel>
                <Popover open={openCountry} onOpenChange={setOpenCountry}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        className={cn(
                          "w-full justify-between font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        <span className="truncate">
                          {field.value
                            ? countryLabelByCode[field.value] ?? field.value
                            : "Select country"}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-[min(400px,90vw)] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search country..." />
                      <CommandEmpty>No country found.</CommandEmpty>
                      <CommandGroup className="max-h-[300px] overflow-auto">
                        {countryOptions.map((country) => (
                          <CommandItem
                            value={country.label}
                            key={country.value}
                            onSelect={() => {
                              field.onChange(country.value);
                              setOpenCountry(false);
                            }}
                          >
                            {country.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="timezone"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Reporting timezone</FormLabel>
                <Popover open={openTimezone} onOpenChange={setOpenTimezone}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        className={cn(
                          "w-full justify-between font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        <span className="truncate">
                          {field.value || "Select timezone"}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-[min(400px,90vw)] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search timezone..." />
                      <CommandEmpty>No timezone found.</CommandEmpty>
                      <CommandGroup className="max-h-[300px] overflow-auto">
                        {timezoneOptions.map((timezone) => (
                          <CommandItem
                            value={timezone.label}
                            key={timezone.value}
                            onSelect={() => {
                              field.onChange(timezone.value);
                              setOpenTimezone(false);
                            }}
                          >
                            {timezone.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {mode === "wizard" ? null : (
          <div className="flex justify-end">
            <Button type="submit" disabled={isSubmitting || isUploading}>
              {isSubmitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {submitLabel}
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
}
